import {sendUserToPage, ViewActionTypes} from "../common/UserViewSettingsActions";
import {ViewIdentifiable} from "../../models/common/ViewSettings";
import {ErrorAction, ReduxAction} from "../../models/common/ActionTypes";
import {ApiReturnType, getUserViewSettingsApiRequest} from "../../models/common/ApiReturnType";
import {
    TimesheetTask,
    TimesheetTaskId,
    TimesheetTaskModule,
    TimesheetTaskProp,
    TimesheetTaskRequest,
    TimesheetTaskRequestProp
} from "../../models/timesheet/TimesheetTask";
import { ofType, StateObservable} from "redux-observable";
import {AppState} from "../../reducers/RootReducer";
import {catchError, filter, map, mergeMap} from "rxjs/operators";
import {APP_CONFIG} from "../../config/GeneralConfig";
import { Observable, of } from 'rxjs';
import {ajax} from 'rxjs/ajax';
import {
    eventNotificationError,
    eventNotificationSuccess
} from "../common/EventNotificationActions";
import {Identifier} from "../../models/common/Identifier";
import {RouteConfig, RouteMethods} from "../../config/RouteConfig";

export type TimesheetTaskActionTypes = TimesheetTasksViewActions
    | FetchTimesheetTaskActionTypes
    | CreateTimesheetTaskActionTypes
    | UpdateTimesheetTaskActionTypes
    | DeleteTimesheetTaskActionTypes
    | RestoreTimesheetTaskActionTypes

const basePath = () => `${ APP_CONFIG.baseWeb }timesheet/tasks/`;
const pathWithId = ( id: number ) => `${ basePath() }${ id }`;

/**
 * FETCH TASKS
 */
export interface FetchTimesheetTasks extends ViewIdentifiable, ReduxAction<typeof FETCH_TIMESHEET_TASKS> {}
export interface FetchTimesheetTasksSuccess extends ViewIdentifiable, ReduxAction<typeof FETCH_TIMESHEET_TASKS_SUCCESS> {
    payload: ApiReturnType< TimesheetTask[] >
}
export interface FetchTimesheetTasksError extends ViewIdentifiable, ErrorAction<typeof FETCH_TIMESHEET_TASKS_ERROR> {}

export const  FETCH_TIMESHEET_TASKS = "FETCH_TIMESHEET_TASKS";
export const  FETCH_TIMESHEET_TASKS_SUCCESS = "FETCH_TIMESHEET_TASKS_SUCCESS";
export const  FETCH_TIMESHEET_TASKS_ERROR = "FETCH_TIMESHEET_TASKS_ERROR";

export const fetchTimesheetTasks = ( viewIdentifier: string ): FetchTimesheetTasks =>
    ({ type: FETCH_TIMESHEET_TASKS, viewIdentifier });
export const fetchTimesheetTasksSuccess = ( viewIdentifier: string, payload: ApiReturnType< TimesheetTask[] >  ): FetchTimesheetTasksSuccess =>
    ({ type: FETCH_TIMESHEET_TASKS_SUCCESS, viewIdentifier, payload });
export const fetchTimesheetTasksError = ( viewIdentifier: string, error: Error ): FetchTimesheetTasksError =>
    ({ type: FETCH_TIMESHEET_TASKS_ERROR, viewIdentifier, error });

export type FetchTimesheetTasksActionTypes = FetchTimesheetTasks | FetchTimesheetTasksSuccess | FetchTimesheetTasksError
export type TimesheetTasksViewActions = ViewActionTypes | FetchTimesheetTasksActionTypes

export const fetchTimesheetTasksEpic = (
    action$: Observable< TimesheetTasksViewActions >, store$: StateObservable< AppState > ) =>
    action$.pipe(
        ofType( FETCH_TIMESHEET_TASKS ),
        filter( action => action.viewIdentifier === TimesheetTaskModule.identifier ),
        mergeMap( action => {
            const request = getUserViewSettingsApiRequest( basePath(), action.viewIdentifier, store$ );

            return ajax( APP_CONFIG.getRequestObject( request.path ) ).pipe(
                map( response => fetchTimesheetTasksSuccess(
                    action.viewIdentifier,
                    response.response as ApiReturnType< TimesheetTask[] >
                ) ),
                catchError( error => of(
                    fetchTimesheetTasksError( action.viewIdentifier, error ),
                    eventNotificationError( error, store$, TimesheetTaskModule.identifier, "fetchTimesheetTasks" )
                ) )
            );
        })
    );

/**
 * FETCH ONE ITEM
 */
export interface FetchTimesheetTaskAction extends TimesheetTaskId, ReduxAction<typeof FETCH_TIMESHEET_TASK> {}
export interface FetchTimesheetTaskSuccessAction extends TimesheetTaskProp, ReduxAction<typeof FETCH_TIMESHEET_TASK_SUCCESS> {}
export interface FetchTimesheetTaskErrorAction extends TimesheetTaskId, ErrorAction<typeof FETCH_TIMESHEET_TASK_ERROR> {}

export type FetchTimesheetTaskActionTypes = FetchTimesheetTaskAction | FetchTimesheetTaskErrorAction | FetchTimesheetTaskSuccessAction

export const FETCH_TIMESHEET_TASK = "FETCH_TIMESHEET_TASK";
export const FETCH_TIMESHEET_TASK_SUCCESS = "FETCH_TIMESHEET_TASK_SUCCESS";
export const FETCH_TIMESHEET_TASK_ERROR = "FETCH_TIMESHEET_TASK_ERROR";

export const fetchTimesheetTask = ( timesheetTaskId: Identifier<number> ): FetchTimesheetTaskAction =>
    ({ type: FETCH_TIMESHEET_TASK, timesheetTaskId });
export const fetchTimesheetTaskSuccess = ( timesheetTask: TimesheetTask ): FetchTimesheetTaskSuccessAction =>
    ({ type: FETCH_TIMESHEET_TASK_SUCCESS, timesheetTask });
export const fetchTimesheetTaskError = ( timesheetTaskId: Identifier<number>, error: Error ): FetchTimesheetTaskErrorAction =>
    ({ type: FETCH_TIMESHEET_TASK_ERROR, error, timesheetTaskId });

export const fetchTimesheetTaskEpic = (
    action$: Observable< FetchTimesheetTaskAction >,
    store$: StateObservable< AppState >
) =>
    action$.pipe(
        ofType( FETCH_TIMESHEET_TASK ),
        mergeMap( action => ajax( APP_CONFIG.getRequestObject( pathWithId( action.timesheetTaskId.rawValue ) ) )
            .pipe(
                map( response => fetchTimesheetTaskSuccess( response.response as TimesheetTask ) ),
                catchError( error => of(
                    fetchTimesheetTaskError( action.timesheetTaskId, error ),
                    eventNotificationError( error, store$, TimesheetTaskModule.identifier, "fetchTimesheetTask" )
                ) )
            )
        )
    );

/**
 * CREATE TASK
 */
export interface CreateTimesheetTaskAction extends TimesheetTaskRequestProp, ReduxAction< typeof CREATE_TIMESHEET_TASK > {}
export interface CreateTimesheetTaskSuccessAction extends TimesheetTaskProp, ReduxAction< typeof CREATE_TIMESHEET_TASK_SUCCESS > {}
export interface CreateTimesheetTaskErrorAction extends ErrorAction<typeof CREATE_TIMESHEET_TASK_ERROR> {}

export type CreateTimesheetTaskActionTypes = CreateTimesheetTaskErrorAction | CreateTimesheetTaskSuccessAction | CreateTimesheetTaskAction

export const CREATE_TIMESHEET_TASK = "CREATE_TIMESHEET_TASK";
export const CREATE_TIMESHEET_TASK_SUCCESS = "CREATE_TIMESHEET_TASK_SUCCESS";
export const CREATE_TIMESHEET_TASK_ERROR = "CREATE_TIMESHEET_TASK_ERROR";

export const createTimesheetTask = ( task: TimesheetTaskRequest ): CreateTimesheetTaskAction =>
    ({ type: CREATE_TIMESHEET_TASK, task });
export const createTimesheetTaskSuccess = ( task: TimesheetTask ): CreateTimesheetTaskSuccessAction =>
    ({ type: CREATE_TIMESHEET_TASK_SUCCESS, timesheetTask: task });
export const createTimesheetTaskError = ( error: Error ): CreateTimesheetTaskErrorAction =>
    ({ type: CREATE_TIMESHEET_TASK_ERROR, error });

export const createTimesheetTaskEpic = (
    action$: Observable< CreateTimesheetTaskAction >,
    store$: StateObservable< AppState >
) =>
    action$.pipe(
        ofType( CREATE_TIMESHEET_TASK ),
        mergeMap( action => ajax( APP_CONFIG.getRequestObject(
                basePath(),
                RouteMethods.post,
                action.task
            ) )
            .pipe(
                mergeMap( response => of(
                    createTimesheetTaskSuccess( response.response as TimesheetTask ),
                    fetchTimesheetTasks( TimesheetTaskModule.identifier ),
                    eventNotificationSuccess(),
                    sendUserToPage(
                        RouteConfig.timesheetTasks.routes.main.path,
                        TimesheetTaskModule.identifier
                    )
                )),
                catchError( error => of(
                    createTimesheetTaskError( error ),
                    eventNotificationError( error, store$, TimesheetTaskModule.identifier, "createTimesheetTask" )
                ) )
            )
        )
    );

/**
 * UPDATE TASK
 */
export interface UpdateTimesheetTaskAction extends TimesheetTaskRequestProp, ReduxAction< typeof UPDATE_TIMESHEET_TASK > {}
export interface UpdateTimesheetTaskSuccessAction extends TimesheetTaskProp, ReduxAction< typeof UPDATE_TIMESHEET_TASK_SUCCESS > {}
export interface UpdateTimesheetTaskErrorAction extends ErrorAction< typeof UPDATE_TIMESHEET_TASK_ERROR > {}

export type UpdateTimesheetTaskActionTypes = UpdateTimesheetTaskAction | UpdateTimesheetTaskErrorAction | UpdateTimesheetTaskSuccessAction

export const UPDATE_TIMESHEET_TASK = "UPDATE_TIMESHEET_TASK";
export const UPDATE_TIMESHEET_TASK_SUCCESS = "UPDATE_TIMESHEET_TASK_SUCCESS";
export const UPDATE_TIMESHEET_TASK_ERROR = "UPDATE_TIMESHEET_TASK_ERROR";

export const updateTimesheetTask = ( task: TimesheetTaskRequest ): UpdateTimesheetTaskAction =>
    ({ type: UPDATE_TIMESHEET_TASK, task });
export const updateTimesheetTaskSuccess = ( task: TimesheetTask ): UpdateTimesheetTaskSuccessAction =>
    ({ type: UPDATE_TIMESHEET_TASK_SUCCESS, timesheetTask: task });
export const updateTimesheetTaskError = ( error: Error ): UpdateTimesheetTaskErrorAction =>
    ({ type: UPDATE_TIMESHEET_TASK_ERROR, error });

export const updateTimesheetTaskEpic = (
    action$: Observable< UpdateTimesheetTaskAction >,
    store$: StateObservable< AppState >
) =>
    action$.pipe(
        ofType( UPDATE_TIMESHEET_TASK ),
        mergeMap( action => ajax( APP_CONFIG.getRequestObject(
                pathWithId( action.task.id ),
                RouteMethods.put,
                action.task
            ) )
            .pipe(
                mergeMap( response => of(
                    updateTimesheetTaskSuccess( response.response as TimesheetTask ),
                    fetchTimesheetTasks(TimesheetTaskModule.identifier),
                    eventNotificationSuccess(),
                    sendUserToPage(
                        RouteConfig.timesheetTasks.routes.main.path,
                        TimesheetTaskModule.identifier
                    )
                ) ),
                catchError( error => of(
                    updateTimesheetTaskError( error ),
                    eventNotificationError( error, store$, TimesheetTaskModule.identifier, "updateTimesheetTask" )
                ) )
            )
        )
    );

/**
 * DELETE TASK
 */
export interface DeleteTimesheetTaskAction extends TimesheetTaskId, ReduxAction<typeof DELETE_TIMESHEET_TASK> {}
export interface DeleteTimesheetTaskSuccessAction extends TimesheetTaskProp,ReduxAction<typeof DELETE_TIMESHEET_TASK_SUCCESS> {}
export interface DeleteTimesheetTaskErrorAction extends ErrorAction<typeof DELETE_TIMESHEET_TASK_ERROR> {}

export type DeleteTimesheetTaskActionTypes = DeleteTimesheetTaskAction | DeleteTimesheetTaskErrorAction | DeleteTimesheetTaskSuccessAction

export const DELETE_TIMESHEET_TASK = "DELETE_TIMESHEET_TASK";
export const DELETE_TIMESHEET_TASK_SUCCESS = "DELETE_TIMESHEET_TASK_SUCCESS";
export const DELETE_TIMESHEET_TASK_ERROR = "DELETE_TIMESHEET_TASK_ERROR";

export const deleteTimesheetTask = ( id: Identifier<number> ): DeleteTimesheetTaskAction =>
    ({ type: DELETE_TIMESHEET_TASK, timesheetTaskId: id });
export const deleteTimesheetTaskSuccess = ( task: TimesheetTask ): DeleteTimesheetTaskSuccessAction =>
    ({ type: DELETE_TIMESHEET_TASK_SUCCESS, timesheetTask: task });
export const deleteTimesheetTaskError = ( error: Error ): DeleteTimesheetTaskErrorAction =>
    ({ type: DELETE_TIMESHEET_TASK_ERROR, error });

export const deleteTimesheetTaskEpic = (
    action$: Observable< DeleteTimesheetTaskAction >,
    store$: StateObservable< AppState >
) =>
    action$.pipe(
        ofType( DELETE_TIMESHEET_TASK ),
        mergeMap( action => ajax( APP_CONFIG.getRequestObject(
                pathWithId( action.timesheetTaskId.rawValue ),
                RouteMethods.delete
            ) )
            .pipe(
                mergeMap( response => of(
                    deleteTimesheetTaskSuccess( response.response as TimesheetTask ),
                    fetchTimesheetTasks(TimesheetTaskModule.identifier),
                    eventNotificationSuccess(),
                    sendUserToPage(
                        RouteConfig.timesheetTasks.routes.main.path,
                        TimesheetTaskModule.identifier
                    )
                ) ),
                catchError( error => of(
                    deleteTimesheetTaskError( error ),
                    eventNotificationError( error, store$, TimesheetTaskModule.identifier, "deleteTimesheetTask" )
                ) )
            )
        )
    );

/**
 * RESTORE TASK
 */
export interface RestoreTimesheetTaskAction extends TimesheetTaskId, ReduxAction<typeof RESTORE_TIMESHEET_TASK> {}
export interface RestoreTimesheetTaskSuccessAction extends TimesheetTaskProp, ReduxAction<typeof RESTORE_TIMESHEET_TASK_SUCCESS> {}
export interface RestoreTimesheetTaskErrorAction extends ErrorAction<typeof RESTORE_TIMESHEET_TASK_ERROR> {}

export type RestoreTimesheetTaskActionTypes = RestoreTimesheetTaskAction | RestoreTimesheetTaskErrorAction | RestoreTimesheetTaskSuccessAction

export const RESTORE_TIMESHEET_TASK = "RESTORE_TIMESHEET_TASK";
export const RESTORE_TIMESHEET_TASK_SUCCESS = "RESTORE_TIMESHEET_TASK_SUCCESS";
export const RESTORE_TIMESHEET_TASK_ERROR = "RESTORE_TIMESHEET_TASK_ERROR";

export const restoreTimesheetTask = ( id: Identifier<number> ): RestoreTimesheetTaskAction =>
    ({ type: RESTORE_TIMESHEET_TASK, timesheetTaskId: id });
export const restoreTimesheetTaskSuccess = ( task: TimesheetTask ): RestoreTimesheetTaskSuccessAction =>
    ({ type: RESTORE_TIMESHEET_TASK_SUCCESS, timesheetTask: task });
export const restoreTimesheetTaskError = ( error: Error ): RestoreTimesheetTaskErrorAction =>
    ({ type: RESTORE_TIMESHEET_TASK_ERROR, error });

export const restoreTimesheetTaskEpic = (
    action$: Observable< RestoreTimesheetTaskAction >,
    store$: StateObservable< AppState >
) =>
    action$.pipe(
        ofType( RESTORE_TIMESHEET_TASK ),
        mergeMap( action => ajax( APP_CONFIG.getRequestObject(
                    pathWithId( action.timesheetTaskId.rawValue ),
                    RouteMethods.patch
            ) )
            .pipe(
                mergeMap( response => of(
                    restoreTimesheetTaskSuccess( response.response as TimesheetTask ),
                    fetchTimesheetTasks(TimesheetTaskModule.identifier),
                    eventNotificationSuccess(),
                    sendUserToPage(
                        RouteConfig.timesheetTasks.routes.main.path,
                        TimesheetTaskModule.identifier
                    )
                ) ),
                catchError(error => of(
                    restoreTimesheetTaskError( error ),
                    eventNotificationError( error, store$, TimesheetTaskModule.identifier, "restoreTimesheetTask" )
                ))
            )
        )
    );